import type { Theme } from '@mui/material';
import { alpha } from '@mui/material';
import type { RowDensity } from './types';

const DENSITY: Record<RowDensity, { headerPy: number; cellPy: number; fontSize: string; headerFontSize: string }> = {
  compact:     { headerPy: 0.6,  cellPy: 0.5,  fontSize: '0.78rem', headerFontSize: '0.68rem' },
  comfortable: { headerPy: 0.85, cellPy: 0.85, fontSize: '13.5px', headerFontSize: '0.7rem'  },
};

export function getDensity(d: RowDensity) { return DENSITY[d]; }

export const HEADER_SX = (theme: Theme, density: RowDensity = 'comfortable') => {
  const d = getDensity(density);
  return {
    py: d.headerPy,
    px: 1.5,
    fontSize: d.headerFontSize,
    fontWeight: 700,
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    borderBottom: `2px solid ${theme.palette.divider}`,
    bgcolor: (theme.palette as any).section?.headerBg || (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backdropFilter: 'blur(8px)',
    userSelect: 'none',
  };
};

export const CELL_SX = (theme: Theme, density: RowDensity = 'comfortable') => {
  const d = getDensity(density);
  return {
    py: d.cellPy,
    px: 1.5,
    fontSize: d.fontSize,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    verticalAlign: 'middle',
  };
};

// Clean rows to match the app's fresh tables (todos/tickets/finances): no zebra
// striping, a soft indigo hover, no heavy left-border accent — just the subtle
// per-row bottom rule from CELL_SX.
export const ROW_SX = (theme: Theme) => ({
  transition: 'background-color 0.12s',
  '&:hover': {
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
  },
  '&.Mui-selected, &.dt-selected': {
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06),
  },
});

export const TABLE_CONTAINER_SX = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
  overflow: 'auto',
  bgcolor: 'background.paper',
});

export const GROUP_HEADER_SX = (theme: Theme) => ({
  py: 0.75,
  px: 1.5,
  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 42,
  zIndex: 1,
});

export const PRIORITY_COLORS: Record<string, string> = {
  low: '#4caf50', normal: '#2196f3', high: '#ff9800', urgent: '#f44336',
};

export const STATUS_COLORS: Record<string, string> = {
  // Canonical W2 ticket statuses — keep in lockstep with
  // frontend/src/constants/ticketStatuses.ts `TICKET_STATUSES[].color`.
  new: '#2196f3',
  assigned: '#42a5f5',
  working: '#ff9800',
  waiting_on_client: '#9c27b0',
  waiting_on_external: '#ab47bc',
  sent_resolution: '#26a69a',
  resolved: '#4caf50',
  closed_unresolved: '#9e9e9e',
  // Inbox-thread / generic display states (different vocabulary; not the
  // ticket enum but used by other status badges). Preserved as-is so the
  // non-ticket surfaces (inbox thread states, scheduled-send states) still
  // pick up colors.
  open: '#2196f3', in_progress: '#ff9800', waiting: '#9c27b0',
  closed: '#9e9e9e', completed: '#4caf50', active: '#66bb6a',
};

export function getStatusColor(status: string, theme: Theme): string {
  const s = theme.palette.status;
  const map: Record<string, string> = {
    open: s.pending, in_progress: s.active, waiting: s.paused,
    closed: 'var(--status-neutral)', completed: s.completed, resolved: s.completed,
    new: s.new, active: s.active, failed: s.failed, paused: s.paused, pending: s.pending,
  };
  return map[status] || 'var(--status-neutral)';
}

export function getPriorityColor(priority: string, theme: Theme): string {
  const p = theme.palette.priority;
  const map: Record<string, string> = { low: p.low, normal: p.normal, high: p.high, urgent: p.urgent };
  return map[priority] || p.normal;
}

export const CHIP_SX = {
  height: 22,
  fontSize: '11px',
  fontWeight: 600,
};

export const AVATAR_SIZE = 28;

export function stringToColor(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360}, 55%, 50%)`;
}

export function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

export function fmtDate(d?: string | null) {
  if (!d) return '—';
  const dt = new Date(d);
  const now = new Date();
  const diff = now.getTime() - dt.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return dt.toLocaleDateString();
}

export const DENSITY_STORAGE_KEY = 'dt_row_density';

export function loadDensity(): RowDensity {
  try {
    const v = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (v === 'compact' || v === 'comfortable') return v;
  } catch { /* ignore */ }
  return 'comfortable';
}

export function saveDensity(d: RowDensity) {
  try { localStorage.setItem(DENSITY_STORAGE_KEY, d); } catch { /* ignore */ }
}

// ── Resizable columns ──
export const MIN_COL_WIDTH = 72;
export const MAX_COL_WIDTH = 720;
export const DEFAULT_COL_WIDTH = 160;
const COLUMN_WIDTHS_STORAGE_PREFIX = 'dt_col_widths_';

export function loadColumnWidths(tableId?: string): Record<string, number> {
  if (!tableId) return {};
  try {
    const v = localStorage.getItem(COLUMN_WIDTHS_STORAGE_PREFIX + tableId);
    if (v) {
      const o = JSON.parse(v);
      if (o && typeof o === 'object') return o as Record<string, number>;
    }
  } catch { /* ignore */ }
  return {};
}

export function saveColumnWidths(tableId: string | undefined, widths: Record<string, number>) {
  if (!tableId) return;
  try { localStorage.setItem(COLUMN_WIDTHS_STORAGE_PREFIX + tableId, JSON.stringify(widths)); } catch { /* ignore */ }
}
